import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Deposant } from '@troc/shared'
import { eur, identiteComplete, modeReversement } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { PastilleReversement } from '../composants/Pastille'
import { CLIENT_VIDE, FormulaireClient } from '../composants/FormulaireClient'
import type { SaisieClient } from '../composants/FormulaireClient'
import { useToast } from '../etat/Toast'
import { useReveil } from '../composants/Volets'

export function Deposants() {
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [requete, setRequete] = useState('')
  const [deposants, setDeposants] = useState<Deposant[]>([])
  /**
   * Combien de fiches répondent à la recherche, au-delà de celles qu'on
   * affiche. Le magasin en compte 18 020 : sans ce total, une liste qui
   * s'arrête aux « A » passe pour une reprise ratée.
   */
  const [total, setTotal] = useState(0)
  const [suiteEnCours, setSuiteEnCours] = useState(false)
  /**
   * Le tri se demande au serveur, pas au tableau : la liste se charge par
   * tranches, et classer les deux cents fiches affichées ne dirait rien des
   * 18 020. Cliquer « Payable » doit ramener le plus gros débiteur du magasin.
   */
  const [tri, setTri] = useState<'nom' | 'articles' | 'payable' | 'attente'>('nom')
  /** Un second clic sur la même colonne inverse le sens plutôt que de le
   *  perdre : « Payable » se lit d'abord du plus gros au plus petit, mais
   *  chercher le plus petit débiteur doit rester possible sans retaper. */
  const [sens, setSens] = useState<'asc' | 'desc'>('asc')
  const changerTri = (colonne: typeof tri) => {
    if (colonne === tri) {
      setSens((s) => (s === 'asc' ? 'desc' : 'asc'))
    } else {
      setTri(colonne)
      setSens(colonne === 'nom' ? 'asc' : 'desc')
    }
  }
  /** La dernière recherche/tri demandés — pour qu'une tranche en vol puisse se
   *  reconnaître périmée si l'utilisateur a changé de recherche entre-temps. */
  const dernierCritere = useRef({ requete, tri, sens })
  useEffect(() => {
    dernierCritere.current = { requete, tri, sens }
  }, [requete, tri, sens])
  /** Sentinelle posée en bas du tableau : la voir, c'est charger la suite. */
  const sentinelle = useRef<HTMLDivElement>(null)
  const [chargement, setChargement] = useState(true)
  const [rafraichir, setRafraichir] = useState(0)
  // Création d'une fiche sans passer par un dépôt : on inscrit parfois un
  // client qui vient se renseigner, avant qu'il ne rapporte quoi que ce soit.
  const [creation, setCreation] = useState<SaisieClient | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      setChargement(true)
      api<{ deposants: Deposant[]; total: number }>(
        `/deposants?tri=${tri}&sens=${sens}${requete ? `&q=${encodeURIComponent(requete)}` : ''}`,
        { signal: controleur.signal },
      )
        .then((r) => {
          setDeposants(r.deposants)
          setTotal(r.total)
        })
        .catch(() => undefined)
        .finally(() => setChargement(false))
    }, 220)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [requete, tri, sens, rafraichir])

  // Les soldes bougent à chaque encaissement : on relit en revenant.
  useReveil('/deposants', () => setRafraichir((n) => n + 1))

  /**
   * La tranche suivante, quand on approche du bas.
   *
   * Le magasin compte 18 020 clients : les envoyer d'un bloc noierait l'iPad,
   * et s'arrêter aux deux cents premiers donne l'impression que la reprise
   * s'est arrêtée à « Albrecq ». La liste se complète donc à mesure qu'on
   * descend, sans bouton ni numéro de page.
   */
  const chargerSuite = useCallback(() => {
    if (suiteEnCours || deposants.length === 0 || deposants.length >= total) return
    setSuiteEnCours(true)
    // Recherche et tri au moment du départ : si l'un des deux change avant que
    // la réponse arrive, elle appartient à une liste qui n'est plus affichée,
    // et on ne veut surtout pas la recoller à la suite d'une autre recherche.
    const requeteDepart = requete
    const triDepart = tri
    const sensDepart = sens
    api<{ deposants: Deposant[] }>(
      `/deposants?tri=${tri}&sens=${sens}&depuis=${deposants.length}${requete ? `&q=${encodeURIComponent(requete)}` : ''}`,
    )
      .then((r) => {
        if (
          requeteDepart !== dernierCritere.current.requete ||
          triDepart !== dernierCritere.current.tri ||
          sensDepart !== dernierCritere.current.sens
        ) {
          return
        }
        // Concaténer sans dédoublonner suffit : la tranche suivante est
        // demandée à partir du nombre déjà affiché, elle ne recoupe rien.
        setDeposants((actuels) => [...actuels, ...r.deposants])
      })
      .catch(() => undefined)
      .finally(() => setSuiteEnCours(false))
  }, [deposants.length, total, requete, tri, sens, suiteEnCours])

  useEffect(() => {
    const cible = sentinelle.current
    if (!cible) return
    const observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((e) => e.isIntersecting)) chargerSuite()
      },
      // On déclenche avant d'avoir touché le fond : la suite est là quand le
      // doigt y arrive.
      { rootMargin: '600px' },
    )
    observateur.observe(cible)
    return () => observateur.disconnect()
  }, [chargerSuite])

  const totalDu = deposants.reduce((somme, d) => somme + Math.max(0, d.soldeCentimes ?? 0), 0)

  const creer = async () => {
    if (!creation || envoi) return
    if (!creation.nom.trim()) {
      alerter('Le nom du client est requis.')
      return
    }
    setEnvoi(true)
    try {
      const reponse = await api<{ deposant: Deposant }>('/deposants', {
        methode: 'POST',
        corps: creation,
      })
      signaler(`Fiche ${reponse.deposant.ref} créée.`)
      setCreation(null)
      naviguer(`/deposants/${reponse.deposant.id}`)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Création impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Ecran
      titre="Déposants"
      sousTitre={
        total > deposants.length
          ? `${deposants.length} fiches sur ${total} — la liste se complète en descendant`
          : `${deposants.length} fiche${deposants.length > 1 ? 's' : ''} · ${eur(totalDu)} à reverser`
      }
      outils={
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            className="saisie saisie-grande"
            style={{ width: 380, maxWidth: '40vw' }}
            placeholder="Rechercher — nom, GSM, localité, n° de client"
            value={requete}
            onChange={(e) => setRequete(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primaire"
            onClick={() => setCreation(CLIENT_VIDE)}
          >
            + Nouveau client
          </button>
        </div>
      }
    >
      {creation && (
        <div className="voile" onClick={() => !envoi && setCreation(null)}>
          <div className="panneau" onClick={(e) => e.stopPropagation()}>
            <div className="panneau-entete">
              <h4 style={{ margin: 0 }}>Nouveau client</h4>
              <button
                type="button"
                className="btn btn-secondaire btn-icone"
                onClick={() => setCreation(null)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <div className="zone-defilante">
              <FormulaireClient valeur={creation} onChange={setCreation} />
            </div>
            <div className="panneau-pied">
              <button
                type="button"
                className="btn btn-secondaire"
                disabled={envoi}
                onClick={() => setCreation(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primaire"
                style={{ marginLeft: 'auto' }}
                disabled={envoi || !creation.nom.trim()}
                onClick={() => void creer()}
              >
                Créer la fiche
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="zone-defilante">
        <table className="tableau">
          <thead>
            <tr>
              <th>N°</th>
              <th>
                <BoutonTri colonne="nom" tri={tri} sens={sens} onTri={changerTri}>
                  Nom
                </BoutonTri>
              </th>
              <th>GSM / téléphone</th>
              <th>Localité</th>
              <th>Identité</th>
              <th className="a-droite">
                <BoutonTri colonne="articles" tri={tri} sens={sens} onTri={changerTri}>
                  En dépôt
                </BoutonTri>
              </th>
              <th className="a-droite">
                <BoutonTri colonne="payable" tri={tri} sens={sens} onTri={changerTri}>
                  Payable
                </BoutonTri>
              </th>
              <th className="a-droite">
                <BoutonTri colonne="attente" tri={tri} sens={sens} onTri={changerTri}>
                  En attente
                </BoutonTri>
              </th>
              <th>Mode</th>
            </tr>
          </thead>
          <tbody>
            {deposants.map((deposant) => {
              const solde = deposant.soldeCentimes ?? 0
              const attente = deposant.soldeAttenteCentimes ?? 0
              return (
                <tr
                  key={deposant.id}
                  className="ligne-cliquable"
                  onClick={() => naviguer(`/deposants/${deposant.id}`)}
                >
                  <td className="ref">{deposant.ref}</td>
                  <td className="titre">
                    {deposant.titre ? `${deposant.titre} ` : ''}
                    {deposant.nom}
                  </td>
                  <td>{deposant.gsm || deposant.tel || '—'}</td>
                  <td>
                    {deposant.codePostal ? `${deposant.codePostal} ` : ''}
                    {deposant.ville || '—'}
                  </td>
                  <td>
                    {identiteComplete(deposant) ? (
                      <span className="coche-ok" title="Identité en règle">
                        ✓
                      </span>
                    ) : (
                      <span className="pastille pastille-a_recuperer">✕ à compléter</span>
                    )}
                  </td>
                  <td className="a-droite">{deposant.articlesEnDepot ?? 0}</td>
                  <td className="a-droite chiffre">{eur(solde)}</td>
                  <td className="a-droite sourdine">{attente > 0 ? eur(attente) : '—'}</td>
                  <td>{solde > 0 && <PastilleReversement mode={modeReversement(solde)} />}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {deposants.length === 0 && (
          <div className="vide">{chargement ? 'Chargement…' : 'Aucun client.'}</div>
        )}
        <div ref={sentinelle} />
        {deposants.length > 0 && deposants.length < total && (
          <div className="vide sourdine">
            {suiteEnCours
              ? 'Chargement des suivants…'
              : `${deposants.length} sur ${total} — continuez à descendre`}
          </div>
        )}
      </div>
    </Ecran>
  )
}

type Colonne = 'nom' | 'articles' | 'payable' | 'attente'

/**
 * Un en-tête de colonne qui trie.
 *
 * Un premier clic choisit la colonne, dans son sens le plus utile par défaut
 * (alphabétique pour le nom, décroissant pour un montant). Un second clic sur
 * la même colonne inverse le sens : c'est ce qui manquait pour retrouver le
 * plus petit débiteur aussi facilement que le plus gros.
 */
function BoutonTri({
  colonne,
  tri,
  sens,
  onTri,
  children,
}: {
  colonne: Colonne
  tri: Colonne
  sens: 'asc' | 'desc'
  onTri: (c: Colonne) => void
  children: React.ReactNode
}) {
  const actif = tri === colonne
  return (
    <button
      type="button"
      className={actif ? 'tri-colonne actif' : 'tri-colonne'}
      aria-pressed={actif}
      onClick={() => onTri(colonne)}
      title={
        actif
          ? `Trier en ordre ${sens === 'asc' ? 'décroissant' : 'croissant'}`
          : 'Trier sur cette colonne'
      }
    >
      {children}
      <span className="tri-fleche" aria-hidden="true">
        {actif ? (sens === 'asc' ? '↑' : '↓') : '⇅'}
      </span>
    </button>
  )
}
